import { useState } from 'react';
import {
  CalendarX,
  Download,
  Upload,
  Plus,
  Play,
  Pencil,
  Trash2,
  Home,
  Clock,
  X,
  Copy,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { PricingRule, OTAChannel, SelectedVariant } from '@/types/revenue-manager';
import { useRoomTypes } from '@/hooks/use-room-inventory';
import { useEvents } from '@/hooks/use-events';
import { useCurrencyFormat } from '@/hooks/use-currency-format';
import { usePermissions } from '@/hooks/use-permissions';

const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const formatLastRun = (dateString: string | undefined): string => {
  if (!dateString) return 'Never';
  try {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}`;
  } catch {
    return 'Never';
  }
};

interface PricingRulesTableProps {
  property: Property;
  rules: PricingRule[];
  otaChannels: OTAChannel[];
  basePrice: number;
  branchId?: string | number;
  orgId?: string | number;
  onCreateRule: () => void;
  onEditRule: (rule: PricingRule) => void;
  onDeleteRule: (ruleId: string) => void;
  onToggleRule: (ruleId: string, isActive: boolean) => void;
  onUpdateRule: (rule: PricingRule) => void;
  onCloneRule: (rule: PricingRule) => void;
  onExecuteRule: (ruleId: string) => void;
  onExport: () => void;
  onImport: () => void;
}

export function PricingRulesTable({
  property,
  rules,
  otaChannels,
  basePrice,
  branchId,
  orgId,
  onCreateRule,
  onEditRule,
  onDeleteRule,
  onToggleRule,
  onUpdateRule,
  onCloneRule,
  onExecuteRule,
  onExport,
  onImport,
}: PricingRulesTableProps) {
  const { canWrite, canDelete } = usePermissions();
  const { data: roomTypesData } = useRoomTypes(branchId);
  const { data: eventsData } = useEvents(String(branchId || ''));
  const { format, symbol } = useCurrencyFormat();
  const events = eventsData?.data || [];
  const roomTypesCount = roomTypesData?.length || 0;

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<{ id: string; name: string } | null>(null);
  
  const handleDeleteClick = (rule: PricingRule) => {
    setRuleToDelete({ id: rule.id, name: rule.name });
    setDeleteDialogOpen(true);
  };
  
  const handleConfirmDelete = () => {
    if (ruleToDelete) {
      onDeleteRule(ruleToDelete.id);
    }
    setDeleteDialogOpen(false);
    setRuleToDelete(null);
  };
  
  const formatCondition = (condition: PricingRule['conditions'][0]) => {
    const operatorMap: Record<string, string> = {
      equals: '=',
      greaterThan: '>',
      lessThan: '<',
      greaterThanOrEqual: '>=',
      lessThanOrEqual: '<=',
    };
    
    if (condition.type === 'daysUntilCheckIn') {
      return `D-${condition.value}`;
    }
    if (condition.type === 'occupancy') {
      return `Occ ${operatorMap[condition.operator]} ${condition.value}%`;
    }
    if (condition.type === 'dayOfWeek') {
      const dayIndex = typeof condition.value === 'number' ? condition.value : parseInt(condition.value as string);
      return `${daysOfWeek[dayIndex] || condition.value}`;
    }
    if (condition.type === 'event') {
      // Use eventDetails if available, otherwise fall back to looking up from events list
      if (condition.eventDetails) {
        return `🎉 ${condition.eventDetails.eventName}`;
      }
      const event = events.find(e => e.id === condition.value);
      return event ? `🎉 ${event.name}` : `Event`;
    }
    return `${condition.type} ${operatorMap[condition.operator]} ${condition.value}`;
  };

  const formatEventDates = (condition: PricingRule['conditions'][0]) => {
    if (condition.type !== 'event') return null;
    
    if (condition.eventDetails) {
      const start = new Date(condition.eventDetails.startDate).toLocaleDateString();
      const end = new Date(condition.eventDetails.endDate).toLocaleDateString();
      return `${start} - ${end}`;
    }
    
    const event = events.find(e => e.id === condition.value);
    if (event) {
      const start = new Date(event.startDate).toLocaleDateString();
      const end = new Date(event.endDate).toLocaleDateString();
      return `${start} - ${end}`;
    }
    
    return null;
  };

  const getScope = (rule: PricingRule) => {
    const roomCount = rule.roomTypes?.length || roomTypesCount;
    const channelCount = rule.otaChannels?.length || otaChannels.length;
    return { rooms: roomCount, channels: channelCount };
  };

  const handleRemoveVariant = (rule: PricingRule, variantId: string) => {
    const updatedVariants = rule.selectedVariants?.filter(v => v.variantId !== variantId) || [];
    onUpdateRule({ ...rule, selectedVariants: updatedVariants });
  };

  const getRoomTypeName = (roomTypeId: string) => {
    const roomType = roomTypesData?.find(rt => String(rt.id) === roomTypeId);
    return roomType?.room_type || roomTypeId;
  };

  const getVariantInfo = (variantId: string) => {
    if (!roomTypesData) return null;
    
    for (const roomType of roomTypesData) {
      const variant = roomType.HotelRoomTypeVarientRelData?.find(
        (v: any) => String(v.id) === String(variantId)
      );
      if (variant) {
        return {
          roomTypeId: String(roomType.id),
          roomTypeName: roomType.room_type,
          variantCode: variant.variant_wise_code || `V${variantId}`,
          variantName: variant.VarientRoomTypeDataRel?.name || 'Unknown Variant'
        };
      }
    }
    return null;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Pricing Rules</h2>
          <p className="text-sm text-muted-foreground">
            Manage dynamic pricing rules for your properties
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onExport}>
            <Download className="mr-2 h-4 w-4" />
            Export Rules
          </Button>
          <Button variant="outline" size="sm" onClick={onImport} disabled={!canWrite}>
            <Upload className="mr-2 h-4 w-4" />
            Import Rules
          </Button>
          <Button size="sm" onClick={onCreateRule} disabled={!canWrite}>
            <Plus className="mr-2 h-4 w-4" />
            Create Rule
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-14">Priority</TableHead>
              <TableHead>Rule Name</TableHead>
              <TableHead>Conditions</TableHead>
              <TableHead>Scope</TableHead>
              <TableHead>Adjustment</TableHead>
              <TableHead>Last Run</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rules.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <div className="flex flex-col items-center justify-center py-16">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
                      <CalendarX className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <p className="mt-4 text-muted-foreground">No pricing rules yet</p>
                    <p className="text-sm text-muted-foreground">
                      Create your first rule to get started
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              rules.map((rule) => {
                const scope = getScope(rule);
                const adjustmentValue = Number(rule.adjustmentValue) || 0;
                const finalPrice = rule.adjustmentType === 'percentage'
                  ? basePrice * (1 + adjustmentValue / 100)
                  : basePrice + adjustmentValue;

                return (
                  <TableRow key={rule.id}>
                    <TableCell>
                      <Badge variant="outline" className="font-mono">
                        {rule.priority}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <CalendarX className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{rule.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-1">
                        {rule.conditions?.map((condition, i) => (
                          <div key={i} className="flex flex-col gap-0.5">
                            <div className="flex items-center gap-1">
                              <Badge
                                variant="secondary"
                                className="font-mono text-xs"
                              >
                                {formatCondition(condition)}
                              </Badge>
                              {i < (rule.conditions?.length || 0) - 1 && condition.logic && (
                                <span className="text-xs text-muted-foreground font-medium">
                                  {condition.logic}
                                </span>
                              )}
                            </div>
                            {condition.type === 'event' && formatEventDates(condition) && (
                              <span className="text-[10px] text-muted-foreground ml-1">
                                {formatEventDates(condition)}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1.5 max-w-[200px]">
                        <Badge variant="outline" className="gap-1 w-fit">
                          <Home className="h-3 w-3" />
                          {scope.rooms}R · {scope.channels}C
                        </Badge>
                        {rule.selectedVariants && rule.selectedVariants.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {rule.selectedVariants.map((variant) => {
                              const variantInfo = getVariantInfo(variant.variantId);
                              const displayCode = variantInfo?.variantCode || variant.variantCode || `V${variant.variantId}`;
                              const displayName = variantInfo?.variantName || variant.variantName || 'Unknown';
                              const roomTypeName = variantInfo?.roomTypeName || getRoomTypeName(variant.roomTypeId);
                              
                              return (
                                <Badge
                                  key={variant.variantId}
                                  variant="secondary"
                                  className="text-[10px] gap-1 pr-1"
                                >
                                  <span className="truncate max-w-[80px]" title={`${roomTypeName} - ${displayName}`}>
                                    {displayCode}
                                  </span>
                                  {/* <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemoveVariant(rule, variant.variantId);
                                    }}
                                    className="hover:bg-muted rounded-full p-0.5"
                                  >
                                    <X className="h-3 w-3" />
                                  </button> */}
                                </Badge>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col items-start">
                        <Badge
                          className={
                            adjustmentValue >= 0
                              ? 'bg-status-active-bg text-status-active hover:bg-status-active-bg'
                              : 'bg-red-100 text-red-600 hover:bg-red-100'
                          }
                        >
                          {adjustmentValue >= 0 ? '+' : ''}
                          {adjustmentValue}
                          {rule.adjustmentType === 'percentage' ? '%' : symbol}
                        </Badge>
                        <span className="text-xs text-muted-foreground mt-1">
                          {format(finalPrice)}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        {formatLastRun(rule.lastRun)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        {rule.processingFrequency === 'manual' && canWrite && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-8 w-8"
                                  onClick={() => onExecuteRule(rule.id)}
                                >
                                  <Play className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Run now</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        <Switch
                          checked={rule.isActive}
                          onCheckedChange={() => onToggleRule(rule.id, rule.isActive)}
                          disabled={!canWrite}
                        />
                        {canWrite && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => onCloneRule(rule)}
                                >
                                  <Copy className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Clone rule</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        {canWrite && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => onEditRule(rule)}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Edit rule</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        {canDelete && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-destructive hover:text-destructive"
                                  onClick={() => handleDeleteClick(rule)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Delete rule</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Pricing Rule</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the rule "{ruleToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
